import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Clock, FileText } from "lucide-react";
import { INDUSTRIES, SOLUTIONS, BRAND } from "../lib/content";
import TermsModal from "./hidroverse/TermsModal";

/**
 * Compact two-band footer.
 *   Band 1 — single horizontal NAP row (Address · Call · Email · Hours).
 *   Band 2 — three slim site-map columns + copyright row.
 * The redundant brand description block was dropped in favour of vertical density.
 */
export default function Footer() {
  const [termsOpen, setTermsOpen] = useState(false);
  const n = BRAND.nap || {};
  const WA_PRE = encodeURIComponent("Hi HidroVerse — I'd like to speak about a water system.");
  const mapsHref = `https://maps.google.com/?q=${encodeURIComponent(
    `${n.street}, ${n.locality}, ${n.region}, ${n.country}`
  )}`;

  return (
    <footer
      className="hv-footer pt-10 md:pt-12 pb-6 px-6 md:px-12 lg:px-20 relative"
      data-testid="site-footer"
    >
      <div
        className="hv-footer-bg"
        style={{ backgroundImage: "url(/library/purification/purification_15.png)" }}
      />
      <div className="relative z-10 max-w-[1600px] mx-auto">
        {/* Band 1 — NAP row, dense, 4 columns desktop / 2 columns mobile */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 pb-6 mb-6 border-b"
          style={{ borderColor: "rgba(255,255,255,0.10)" }}
          data-testid="footer-nap"
        >
          <div data-testid="footer-nap-address">
            <div
              className="eyebrow text-white/50 mb-2 flex items-center gap-1.5"
              style={{ fontSize: "0.58rem", letterSpacing: "0.24em" }}
            >
              <MapPin size={10} aria-hidden style={{ color: "#1FB6C1" }} /> Visit
            </div>
            <a
              href={mapsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="not-italic text-[0.84rem] text-white/85 font-light leading-snug hv-link block"
              data-testid="footer-nap-maps"
            >
              {n.street}, {n.locality}, {n.postal}
            </a>
          </div>
          <div data-testid="footer-nap-contact">
            <div
              className="eyebrow text-white/50 mb-2 flex items-center gap-1.5"
              style={{ fontSize: "0.58rem", letterSpacing: "0.24em" }}
            >
              <Phone size={10} aria-hidden style={{ color: "#1FB6C1" }} /> Call
            </div>
            <a
              href={`tel:${BRAND.phoneIntl}`}
              className="hv-link text-[0.84rem] text-white/85 font-light leading-snug block"
              data-testid="footer-tel"
            >
              {BRAND.phone}
            </a>
            <a
              href={`https://wa.me/${BRAND.whatsapp}?text=${WA_PRE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="hv-link text-[0.74rem] text-white/55 font-light block mt-1"
              data-testid="footer-whatsapp"
            >
              WhatsApp — same number
            </a>
          </div>
          <div data-testid="footer-nap-email">
            <div
              className="eyebrow text-white/50 mb-2 flex items-center gap-1.5"
              style={{ fontSize: "0.58rem", letterSpacing: "0.24em" }}
            >
              <Mail size={10} aria-hidden style={{ color: "#1FB6C1" }} /> Email
            </div>
            <a
              href={`mailto:${BRAND.email}`}
              className="hv-link text-[0.84rem] text-white/85 font-light leading-snug break-all"
              data-testid="footer-email"
            >
              {BRAND.email}
            </a>
          </div>
          <div data-testid="footer-nap-hours">
            <div
              className="eyebrow text-white/50 mb-2 flex items-center gap-1.5"
              style={{ fontSize: "0.58rem", letterSpacing: "0.24em" }}
            >
              <Clock size={10} aria-hidden style={{ color: "#1FB6C1" }} /> Hours
            </div>
            <div className="text-[0.84rem] text-white/85 font-light leading-snug">
              Mon–Fri 08:00–17:00
            </div>
            <div className="text-[0.72rem] text-white/55 font-light mt-1">
              Critical-response SLA · 24/7
            </div>
          </div>
        </div>

        {/* Band 2 — slim site map */}
        <div className="grid grid-cols-3 gap-6 md:gap-10">
          <div data-testid="footer-col-industries">
            <div className="eyebrow text-white/50 mb-3" style={{ fontSize: "0.58rem", letterSpacing: "0.24em" }}>
              Industries
            </div>
            <ul className="space-y-1.5">
              {INDUSTRIES.slice(0, 6).map((i) => (
                <li key={i.slug}>
                  <Link to={`/industries/${i.slug}`} className="hv-link text-[0.82rem] text-white/80">
                    {i.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="footer-col-solutions">
            <div className="eyebrow text-white/50 mb-3" style={{ fontSize: "0.58rem", letterSpacing: "0.24em" }}>
              Services
            </div>
            <ul className="space-y-1.5">
              {SOLUTIONS.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link to={`/services/${s.slug}`} className="hv-link text-[0.82rem] text-white/80">
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div data-testid="footer-col-company">
            <div className="eyebrow text-white/50 mb-3" style={{ fontSize: "0.58rem", letterSpacing: "0.24em" }}>
              Company
            </div>
            <ul className="space-y-1.5">
              <li><Link to="/projects" className="hv-link text-[0.82rem] text-white/80">Portfolio</Link></li>
              <li><Link to="/team" className="hv-link text-[0.82rem] text-white/80">Team</Link></li>
              <li><Link to="/insights" className="hv-link text-[0.82rem] text-white/80">Insights</Link></li>
              <li><Link to="/compliance" className="hv-link text-[0.82rem] text-white/80">Compliance</Link></li>
              <li><Link to="/portal" className="hv-link text-[0.82rem] text-white/80">Client Portal</Link></li>
              <li><Link to="/contact" className="hv-link text-[0.82rem] text-white/80">Contact</Link></li>
            </ul>
          </div>
        </div>

        {/* Social row */}
        {BRAND.social && (
          <div
            className="mt-8 pt-5 border-t flex flex-wrap items-center justify-between gap-3"
            style={{ borderColor: "rgba(255,255,255,0.10)" }}
            data-testid="footer-socials"
          >
            <div
              className="font-mono uppercase"
              style={{ fontSize: "0.6rem", letterSpacing: "0.24em", color: "rgba(255,255,255,0.5)", fontWeight: 700 }}
            >
              Follow HidroVerse
            </div>
            <div className="flex items-center gap-2">
              {BRAND.social.linkedin && (
                <a
                  href={BRAND.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  data-testid="footer-social-linkedin"
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                  style={{ background: "rgba(255,255,255,0.06)", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.10)" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
                </a>
              )}
              {BRAND.social.instagram && (
                <a
                  href={BRAND.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  data-testid="footer-social-instagram"
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                  style={{ background: "rgba(255,255,255,0.06)", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.10)" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </a>
              )}
              {BRAND.social.facebook && (
                <a
                  href={BRAND.social.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  data-testid="footer-social-facebook"
                  className="w-9 h-9 inline-flex items-center justify-center rounded-full transition-transform hover:scale-110"
                  style={{ background: "rgba(255,255,255,0.06)", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.10)" }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M9 8h-3v4h3v12h5v-12h3.642l.358-4h-4v-1.667c0-.955.192-1.333 1.115-1.333h2.885v-5h-3.808c-3.596 0-5.192 1.583-5.192 4.615v3.385z"/></svg>
                </a>
              )}
            </div>
          </div>
        )}

        {/* Copyright + powered-by — one slim row */}
        <div
          className="mt-8 pt-5 border-t border-white/10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2"
          style={{ fontSize: "0.62rem", letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.42)" }}
        >
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
            <span>© {new Date().getFullYear()} HidroVerse™ · Universal Water Systems</span>
            <button
              type="button"
              onClick={() => setTermsOpen(true)}
              className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
              style={{ fontSize: "0.62rem", letterSpacing: "0.16em" }}
              data-testid="footer-terms-link"
              aria-label="View Terms and Conditions"
            >
              <FileText size={11} aria-hidden /> Terms &amp; Conditions
            </button>
          </div>
          <a
            href="https://geminii-ai.com/space"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-white transition-colors"
            data-testid="footer-powered-by"
          >
            <span>Powered by</span>
            <span className="text-white/75 tracking-[0.2em]">Geminii Space</span>
            <span aria-hidden>↗</span>
          </a>
        </div>
      </div>
      <TermsModal open={termsOpen} onClose={() => setTermsOpen(false)} />
    </footer>
  );
}
